import React, {useEffect, useState} from "react";
import PlotlyPlot from "../../../../common/components/PlotlyPlot";
import {loadProtocolConnMapData} from "../../services/protocols";
import {PlotParams} from "react-plotly.js";
import {IProtocol} from "../../../types/protocol";
import {ICompany} from "../../../../common/types";
import {BiomarkerNames} from "../../types/biomarkerNames";
import {Box, Loader, Select, NumberInput, Button, Text} from "@mantine/core";

interface ConnectivityMapChartProps {
    activeCompany: ICompany;
    selectedProtocol: IProtocol | undefined;
    refreshTrigger?: number
}

export const ConnectivityMapChart: React.FC<ConnectivityMapChartProps> = ({activeCompany, selectedProtocol, refreshTrigger}) => {
    const [connMapData, setConnMapData] = useState<PlotParams | undefined>()
    const [loading, setLoading] = useState<boolean>(false)
    const [frequencyBand, setFrequencyBand] = useState<string>("beta")
    const [connectivityMethod, setConnectivityMethod] = useState<string>(BiomarkerNames.CONNECTIVITY_WPLI)
    const [selectedStim, setSelectedStim] = useState<string | null>(null)
    const [stimOptions, setStimOptions] = useState<Array<{value:string; label:string}>>([])

    // New: quantile UI state and appliedQuantile which actually controls plotting
    const [quantileInput, setQuantileInput] = useState<number>(0.5)
    const [appliedQuantile, setAppliedQuantile] = useState<number>(0.5)

    const freqOptions = [
        { value: "delta", label: "Delta" },
        { value: "theta", label: "Theta" },
        { value: "alpha", label: "Alpha" },
        { value: "beta", label: "Beta" },
        { value: "gamma", label: "Gamma" },
        { value: "all", label: "All" },
    ]

    const connectivityMethodOptions = [
        { value: BiomarkerNames.CONNECTIVITY_WPLI, label: "wPLI" },
        { value: BiomarkerNames.CONNECTIVITY_TPLI, label: "tPLI" },
    ]

    // White to blue to red color scale
    const valueToColor = (v: number, vmin: number, vmax: number) => {
        if (!isFinite(v) || !isFinite(vmin) || !isFinite(vmax)) return "#cccccc";
        const tRaw = (v - vmin) / (vmax - vmin || 1);
        const t = Math.max(0, Math.min(1, tRaw));
        const white = {r: 255, g: 255, b: 255};
        const blue = {r: 59, g: 76, b: 192}; // #3b4cc0
        const red = {r: 180, g: 4, b: 38}; // #b40426
        let c;
        if (t < 0.5) {
            const u = t * 2;
            c = {
                r: Math.round(white.r + (blue.r - white.r) * u),
                g: Math.round(white.g + (blue.g - white.g) * u),
                b: Math.round(white.b + (blue.b - white.b) * u),
            };
        } else {
            const u = (t - 0.5) * 2;
            c = {
                r: Math.round(blue.r + (red.r - blue.r) * u),
                g: Math.round(blue.g + (red.g - blue.g) * u),
                b: Math.round(blue.b + (red.b - blue.b) * u),
            };
        }
        return `rgb(${c.r},${c.g},${c.b})`;
    }

    const valueToWidth = (v: number, vmin: number, vmax: number) => {
        if (!isFinite(v)) return 1;
        const tRaw = (v - vmin) / (vmax - vmin || 1);
        const t = Math.max(0, Math.min(1, tRaw));
        return 2 + t * 4; // range 1..7
    }

    // helper for quantile interpolation
    const computeQuantile = (arr: number[], q: number) => {
        if (!arr || arr.length === 0) return 0.01;
        const sorted = [...arr].sort((a,b) => a - b);
        const m = sorted.length;
        if (m === 1) return sorted[0];
        const pos = q * (m - 1);
        const lo = Math.floor(pos);
        const hi = Math.ceil(pos);
        if (hi === lo) return sorted[lo];
        const w = pos - lo;
        return sorted[lo] * (1 - w) + sorted[hi] * w;
    }

    useEffect(() => {
        if (!selectedProtocol) return;
        setLoading(true)

        loadProtocolConnMapData(activeCompany.id, selectedProtocol, connectivityMethod, [frequencyBand])
            .then((resp: any) => {
                // data.data expected to be array of [stim_condition, channel_names, x_coords, y_coords, pre_values, post_values]
                const rows = resp.data || [];

                const stimSet = new Set<string>();
                const stimMap = new Map<string, { channel_names: string[]; x_coords: number[]; y_coords: number[]; pre_values: number[]; post_values: number[] }>();
                rows.forEach(([stim, channel_names, x_coords, y_coords, pre_values, post_values]: [string, string[], number[], number[], number[], number[]]) => {
                    stimSet.add(stim);
                    stimMap.set(stim, { channel_names, x_coords, y_coords, pre_values, post_values });
                });

                const stimOrder = Array.from(stimSet);
                setStimOptions(stimOrder.map(s => ({ value: s, label: s })));

                // if no selectedStim yet, pick first
                if (!selectedStim && stimOrder.length) setSelectedStim(stimOrder[0]);

                // build plot for currently selected stim (or first)
                const useStim = selectedStim || stimOrder[0];
                const entry = useStim ? stimMap.get(useStim) : undefined;

                // base layout from server (template)
                const baseLayout = resp.figure?.layout || {};

                if (!entry) {
                    setConnMapData({ layout: { ...baseLayout, autosize: true }, data: [], config: { responsive: true } });
                    return;
                }

                const {channel_names, x_coords, y_coords, pre_values, post_values} = entry;
                const n = channel_names.length;

                // compute global min/max across both value sets
                const allVals = [...(pre_values || []), ...(post_values || [])];

                // Determine threshold based on appliedQuantile using pre_values > 0.01
                const preAbove = (pre_values || []).filter(v => isFinite(v) && v > 0.01);
                const threshold = preAbove.length ? computeQuantile(preAbove, appliedQuantile) : 0.01;
                //const allAbove = (allVals || []).filter(v => isFinite(v) && v > 0.01);
                //const threshold = allAbove.length ? computeQuantile(allAbove, appliedQuantile) : 0.01;

                // Build filtered list of values above threshold across both sets for color scaling
                const allValsAboveThreshold = allVals.filter(v => isFinite(v) && v >= threshold);
                const minVal = allValsAboveThreshold.length ? Math.min(...allValsAboveThreshold) : (allVals.length ? Math.min(...allVals) : 0);
                const maxVal = allValsAboveThreshold.length ? Math.max(...allValsAboveThreshold) : (allVals.length ? Math.max(...allVals) : 1);

                // prepare traces: two marker scatters (pre, post) and many line traces for connectivity
                const traces: any[] = [];

                // Pre connectivity lines
                let k = 0;
                for (let i = 0; i < n - 1; i++) {
                    for (let j = i + 1; j < n; j++) {
                        const val = pre_values[k];
                        // Only plot if value meets threshold
                        if (isFinite(val) && val >= threshold) {
                            traces.push({
                                ...(resp.figure?.data?.[0] || {}),
                                x: [x_coords[i], x_coords[j]],
                                y: [y_coords[i], y_coords[j]],
                                mode: 'lines',
                                line: { color: valueToColor(val, minVal, maxVal), width: valueToWidth(val, minVal, maxVal) },
                                opacity: 0.7,
                                hoverinfo: 'text',
                                hovertext: `${channel_names[i]} - ${channel_names[j]}: ${val}`,
                                name: 'Connectivity (pre)',
                            });
                        }
                        k++;
                    }
                }

                // Pre subplot markers (assume subplot 1)
                traces.push({
                    ...(resp.figure?.data?.[1] || {}),
                    x: x_coords,
                    y: y_coords,
                    mode: 'markers+text',
                    text: channel_names,
                    textposition: 'top center',
                    marker: { color: 'white', size: 8, line: { color: 'black', width: 1 } },
                    name: 'Sensors (pre)',
                });

                // Post connectivity lines
                k = 0;
                for (let i = 0; i < n - 1; i++) {
                    for (let j = i + 1; j < n; j++) {
                        const val = post_values[k];
                        // Only plot if value meets threshold
                        if (isFinite(val) && val >= threshold) {
                            traces.push({
                                ...(resp.figure?.data?.[2] || {}),
                                x: [x_coords[i], x_coords[j]],
                                y: [y_coords[i], y_coords[j]],
                                mode: 'lines',
                                line: { color: valueToColor(val, minVal, maxVal), width: valueToWidth(val, minVal, maxVal) },
                                opacity: 0.7,
                                hoverinfo: 'text',
                                hovertext: `${channel_names[i]} - ${channel_names[j]}: ${val}`,
                                name: 'Connectivity (post)',
                                xaxis: 'x2',
                                yaxis: 'y2',
                            });
                        }
                        k++;
                    }
                }

                // Post subplot markers (assume subplot 2)
                traces.push({
                    ...(resp.figure?.data?.[3] || {}),
                    x: x_coords,
                    y: y_coords,
                    mode: 'markers+text',
                    text: channel_names,
                    textposition: 'top center',
                    marker: { color: 'white', size: 8, line: { color: 'black', width: 1 } },
                    name: 'Sensors (post)',
                    xaxis: 'x2',
                    yaxis: 'y2',
                });

                // Add a hidden marker trace solely to render a single shared colorbar legend
                const colorscale = [
                    [0, 'rgb(255,255,255)'],
                    [0.5, 'rgb(59,76,192)'],
                    [1, 'rgb(180,4,38)'],
                ];
                const colorbarTrace = {
                    x: [x_coords && x_coords.length ? x_coords[0] : 0],
                    y: [y_coords && y_coords.length ? y_coords[0] : 0],
                    mode: 'markers',
                    marker: {
                        colorscale,
                        color: [minVal],
                        cmin: minVal,
                        cmax: maxVal,
                        showscale: true,
                        colorbar: { title: 'Connectivity', titleside: 'right' },
                        opacity: 0,
                        size: 0,
                    },
                    hoverinfo: 'none',
                    showlegend: false,
                };
                traces.push(colorbarTrace);

                // Use server-provided layout as base but ensure responsive and margins
                const layout = {
                    ...baseLayout,
                    autosize: true,
                    margin: baseLayout?.margin || { l: 50, r: 50, t: 50, b: 50 },
                };

                setConnMapData({ layout, data: traces, config: { responsive: true } });
            })
            .catch(() => {
                // swallow — caller handles notifications
            })
            .finally(() => setLoading(false));

    }, [selectedProtocol, refreshTrigger, frequencyBand, connectivityMethod, selectedStim, appliedQuantile]);

    const renderLoading = () => {
        return (
            <Box style={{position: "absolute", zIndex: "10", top: "calc(50% - 2em)", left: "calc(50% - 2em)"}}>
                <Loader size="4em" color="var(--bios-strong-accent)"/>
            </Box>
        )
    }

    return (
        <Box style={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            <Box style={{flex: '0 0 auto', display: 'flex', gap: '1rem', padding: '0.5rem', alignItems: 'center'}}>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Frequency band</Text>
                    <Select
                        data={freqOptions}
                        value={frequencyBand}
                        onChange={(v) => setFrequencyBand(v || "all")}
                        style={{width: 180}}
                    />
                </Box>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Connectivity method</Text>
                    <Select
                        data={connectivityMethodOptions}
                        value={connectivityMethod}
                        onChange={(v) => setConnectivityMethod(v || BiomarkerNames.CONNECTIVITY_WPLI)}
                        style={{width: 180}}
                    />
                </Box>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Stimulation</Text>
                    <Select
                        data={stimOptions}
                        value={selectedStim}
                        onChange={(v) => setSelectedStim(v)}
                        style={{width: 180}}
                    />
                </Box>

                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Quantile (0 - 0.99)</Text>
                    <Box style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                        {/* Quantile input and update button — changing the input won't update the chart until Update is pressed */}
                        <NumberInput
                            value={quantileInput}
                            onChange={(v) => setQuantileInput(typeof v === 'number' ? v : 0)}
                            min={0}
                            max={0.99}
                            step={0.01}
                            precision={2}
                            style={{width: 180}}
                        />

                        <Button onClick={() => setAppliedQuantile(quantileInput)}>Update</Button>
                    </Box>
                </Box>
            </Box>

            <Box style={{flex: '1 1 auto', minHeight: 0, position: 'relative', overflow: 'hidden'}}>
                {connMapData ? (
                    <Box style={{height: '100%', width: '100%'}}>
                        <PlotlyPlot {...connMapData}/>
                        {loading ? renderLoading() : null}
                    </Box>
                ) : (
                    <Box style={{height: '100%', position: 'relative'}}>
                        {renderLoading()}
                    </Box>
                )}
            </Box>
        </Box>
    )
}
